import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, arrayUnion, arrayRemove, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const GroupContext = createContext();

export const useGroup = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroup must be used within a GroupProvider');
  }
  return context;
};

export const GroupProvider = ({ children, user }) => {
  const [currentGroup, setCurrentGroup] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper to get user's display name
  const getUserDisplayName = useCallback(async () => {
    if (!user) return null;
    
    if (user.displayName) {
      return user.displayName;
    }
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists() && userDoc.data().displayName) {
        return userDoc.data().displayName;
      }
    } catch (err) {
      console.warn('Could not fetch user display name:', err);
    }
    
    return user.email?.split('@')[0] || 'Unknown';
  }, [user]);

  // =====================================================
  // REAL-TIME GROUP LISTENER (FIXED)
  // Previously used getDocs (one-time fetch) which meant
  // newly created groups were never picked up automatically.
  // Now uses onSnapshot so the context stays in sync.
  // =====================================================
  useEffect(() => {
    if (!user) {
      setCurrentGroup(null);
      setUserGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    console.log('🔥 GroupContext: setting up listener for user:', user.uid);

    const groupsRef = collection(db, 'groups');
    // Use array-contains on a flat memberIds array — no composite index needed.
    // The old query (where members.UID.role 'in' [...]) required a Firestore
    // composite index that didn't exist, causing the snapshot to silently fail.
    const q = query(groupsRef, where('memberIds', 'array-contains', user.uid));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        console.log('🔥 GroupContext: snapshot fired, docs:', snapshot.docs.length, 'fromCache:', snapshot.metadata.fromCache);
        try {
          const groups = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setUserGroups(groups);

          // Get user's preferred current group
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          const userData = userDoc.exists() ? userDoc.data() : null;

          if (groups.length === 0) {
            console.log('🔥 GroupContext: no groups found for user');
            setCurrentGroup(null);
          } else if (userData?.currentGroupId && groups.find(g => g.id === userData.currentGroupId)) {
            // Use saved preference
            setCurrentGroup(groups.find(g => g.id === userData.currentGroupId));
          } else {
            // Default to first group and save preference
            setCurrentGroup(groups[0]);
            await setDoc(userDocRef, { currentGroupId: groups[0].id }, { merge: true });
          }
        } catch (err) {
          console.error('Error processing groups snapshot:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('🔥 GroupContext: snapshot ERROR:', err.code, err.message);
        setError(err.message);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount or user change
    return () => unsubscribe();
  }, [user]);

  // Create a new group (band)
  const createGroup = useCallback(async (groupName) => {
    if (!user) throw new Error('Must be logged in');
    
    try {
      const displayName = await getUserDisplayName();
      
      const groupRef = doc(collection(db, 'groups'));
      const groupData = {
        name: groupName,
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
        memberIds: [user.uid],   // flat array for efficient querying (no index needed)
        members: {
          [user.uid]: {
            odataid: user.uid,
            odatauserId: user.uid,
            role: 'admin',
            email: user.email,
            name: displayName,
            displayName: displayName,
            joinedAt: new Date().toISOString()
          }
        }
      };

      await setDoc(groupRef, groupData);

      // Initialize empty data document for the group
      const dataRef = doc(db, 'groups', groupRef.id, 'data', 'main');
      await setDoc(dataRef, {
        shows: {},
        contacts: [],
        expenses: [],
        inventory: [],
        polls: [],
        lastUpdated: new Date().toISOString()
      });

      // Update user's current group preference
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, { currentGroupId: groupRef.id }, { merge: true });

      // NOTE: No need to manually call setUserGroups or setCurrentGroup here
      // The onSnapshot listener above will fire automatically when the new
      // group document is created, and will update state for us.

      return { id: groupRef.id, ...groupData };
    } catch (err) {
      console.error('Error creating group:', err);
      throw err;
    }
  }, [user, getUserDisplayName]);

  // Switch to a different group
  const switchGroup = useCallback(async (groupId) => {
    if (!user) return;

    const group = userGroups.find(g => g.id === groupId);
    if (!group) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, { currentGroupId: groupId }, { merge: true });
      setCurrentGroup(group);
    } catch (err) {
      console.error('Error switching group:', err);
      throw err;
    }
  }, [user, userGroups]);

  // Generate invite link/code
  const createInvite = useCallback(async (email = null) => {
    if (!user || !currentGroup) throw new Error('No group selected');
    
    const memberInfo = currentGroup.members[user.uid];
    if (memberInfo?.role !== 'admin') {
      throw new Error('Only admins can invite members');
    }

    try {
      const inviteCode = Math.random().toString(36).substring(2, 10).toUpperCase();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const groupRef = doc(db, 'groups', currentGroup.id);
      await updateDoc(groupRef, {
        [`invites.${inviteCode}`]: {
          email: email || null,
          createdBy: user.uid,
          createdAt: new Date().toISOString(),
          expiresAt: expiresAt.toISOString()
        }
      });

      return {
        code: inviteCode,
        link: `${window.location.origin}/join/${inviteCode}`
      };
    } catch (err) {
      console.error('Error creating invite:', err);
      throw err;
    }
  }, [user, currentGroup]);

  // Join a group via invite code
  const joinGroup = useCallback(async (inviteCode) => {
    if (!user) throw new Error('Must be logged in');

    try {
      const displayName = await getUserDisplayName();
      
      const groupsRef = collection(db, 'groups');
      const groupsSnapshot = await getDocs(groupsRef);
      
      let targetGroup = null;
      let invite = null;

      groupsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.invites && data.invites[inviteCode]) {
          targetGroup = { id: doc.id, ...data };
          invite = data.invites[inviteCode];
        }
      });

      if (!targetGroup) {
        throw new Error('Invalid invite code');
      }

      if (new Date(invite.expiresAt) < new Date()) {
        throw new Error('Invite code has expired');
      }

      if (invite.email && invite.email.toLowerCase() !== user.email.toLowerCase()) {
        throw new Error('This invite is for a different email address');
      }

      if (targetGroup.members[user.uid]) {
        throw new Error('You are already a member of this group');
      }

      const groupRef = doc(db, 'groups', targetGroup.id);
      const memberData = {
        odataid: user.uid,
        odatauserId: user.uid,
        role: 'member',
        email: user.email,
        name: displayName,
        displayName: displayName,
        joinedAt: new Date().toISOString()
      };
      
      await updateDoc(groupRef, {
        [`members.${user.uid}`]: memberData,
        memberIds: arrayUnion(user.uid),     // keep flat array in sync
        [`invites.${inviteCode}`]: null
      });

      // Update user's current group preference
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, { currentGroupId: targetGroup.id }, { merge: true });

      // NOTE: onSnapshot will handle updating userGroups and currentGroup automatically

      return { ...targetGroup, members: { ...targetGroup.members, [user.uid]: memberData } };
    } catch (err) {
      console.error('Error joining group:', err);
      throw err;
    }
  }, [user, getUserDisplayName]);

  // Update member's display name
  const updateMemberName = useCallback(async (newName) => {
    if (!user || !currentGroup) return;
    
    try {
      const groupRef = doc(db, 'groups', currentGroup.id);
      await updateDoc(groupRef, {
        [`members.${user.uid}.name`]: newName,
        [`members.${user.uid}.displayName`]: newName
      });

      // Update local state optimistically (onSnapshot will also confirm)
      const updatedMembers = {
        ...currentGroup.members,
        [user.uid]: {
          ...currentGroup.members[user.uid],
          name: newName,
          displayName: newName
        }
      };
      
      const updatedGroup = { ...currentGroup, members: updatedMembers };
      setCurrentGroup(updatedGroup);
      setUserGroups(prev => prev.map(g => g.id === currentGroup.id ? updatedGroup : g));
    } catch (err) {
      console.error('Error updating member name:', err);
      throw err;
    }
  }, [user, currentGroup]);

  // Remove a member from group (admin only)
  const removeMember = useCallback(async (memberId) => {
    if (!user || !currentGroup) throw new Error('No group selected');
    
    const memberInfo = currentGroup.members[user.uid];
    if (memberInfo?.role !== 'admin') {
      throw new Error('Only admins can remove members');
    }

    if (memberId === user.uid) {
      throw new Error('Cannot remove yourself. Transfer ownership first or delete the group.');
    }

    try {
      const groupRef = doc(db, 'groups', currentGroup.id);
      await updateDoc(groupRef, {
        [`members.${memberId}`]: null,
        memberIds: arrayRemove(memberId)   // keep flat array in sync
      });

      // Optimistic update (onSnapshot will confirm)
      const updatedMembers = { ...currentGroup.members };
      delete updatedMembers[memberId];
      
      const updatedGroup = { ...currentGroup, members: updatedMembers };
      setCurrentGroup(updatedGroup);
      setUserGroups(prev => prev.map(g => g.id === currentGroup.id ? updatedGroup : g));
    } catch (err) {
      console.error('Error removing member:', err);
      throw err;
    }
  }, [user, currentGroup]);

  // Leave a group
  const leaveGroup = useCallback(async (groupId) => {
    if (!user) throw new Error('Must be logged in');

    const group = userGroups.find(g => g.id === groupId);
    if (!group) throw new Error('Group not found');

    const admins = Object.entries(group.members).filter(([_, m]) => m.role === 'admin');
    if (admins.length === 1 && admins[0][0] === user.uid) {
      throw new Error('Cannot leave group. You are the only admin. Transfer ownership or delete the group.');
    }

    try {
      const groupRef = doc(db, 'groups', groupId);
      await updateDoc(groupRef, {
        [`members.${user.uid}`]: null,
        memberIds: arrayRemove(user.uid)   // keep flat array in sync
      });

      // onSnapshot will handle removing group from userGroups
      // But we still need to handle switching if leaving current group
      if (currentGroup?.id === groupId) {
        const remainingGroups = userGroups.filter(g => g.id !== groupId);
        if (remainingGroups.length > 0) {
          await switchGroup(remainingGroups[0].id);
        } else {
          setCurrentGroup(null);
        }
      }
    } catch (err) {
      console.error('Error leaving group:', err);
      throw err;
    }
  }, [user, userGroups, currentGroup, switchGroup]);

  // Update group name
  const updateGroupName = useCallback(async (newName) => {
    if (!user || !currentGroup) throw new Error('No group selected');
    
    const memberInfo = currentGroup.members[user.uid];
    if (memberInfo?.role !== 'admin') {
      throw new Error('Only admins can rename the group');
    }

    try {
      const groupRef = doc(db, 'groups', currentGroup.id);
      await updateDoc(groupRef, { name: newName });

      // Optimistic update (onSnapshot will confirm)
      const updatedGroup = { ...currentGroup, name: newName };
      setCurrentGroup(updatedGroup);
      setUserGroups(prev => prev.map(g => g.id === currentGroup.id ? updatedGroup : g));
    } catch (err) {
      console.error('Error updating group name:', err);
      throw err;
    }
  }, [user, currentGroup]);

  const isAdmin = currentGroup?.members?.[user?.uid]?.role === 'admin';

  const value = {
    currentGroup,
    userGroups,
    loading,
    error,
    isAdmin,
    createGroup,
    switchGroup,
    createInvite,
    joinGroup,
    removeMember,
    leaveGroup,
    updateGroupName,
    updateMemberName,
    refreshGroups: () => {
      setLoading(true);
      setTimeout(() => setLoading(false), 0);
    }
  };

  return (
    <GroupContext.Provider value={value}>
      {children}
    </GroupContext.Provider>
  );
};

export default GroupContext;